import {useCallback, useEffect} from "react";
import {invalidateResource, mutateCache} from "api/resourceCache";
import {useCachedResource} from "api/useCachedResource";
import {PUBLISHED_TERMS_VERSION} from "../../terms/publishedTermsVersion";
import {UnusedPurchasesApi} from "./UnusedPurchasesApi";
import {
	conversionLeavesList,
	type ConvertedCredit,
	type ConvertResult,
	convertOutcomeOf,
	listingWithout,
	NO_PURCHASES,
	type PurchaseListing,
	type UnusedPurchase,
	type UsedPurchase,
} from "./unusedPurchases";
import {UNUSED_PURCHASES_CACHE_KEY} from "./unusedPurchasesCache";

export interface UnusedPurchasesVM {
	/**
	 * The account's paid, unused purchases of BOTH families, newest payment first; empty until read, and after a
	 * failure. A credit is listed so its buyer can withdraw from it, but nothing can spend it: a surface that offers
	 * "Use it" reads `unusedPurchases.spendablePurchasesOf` of this, never this.
	 */
	purchases: UnusedPurchase[];
	/** The purchases already used on a transfer whose statutory withdrawal period is still open. */
	withdrawableUsed: UsedPurchase[];
	/**
	 * The credit the account's converted purchases left, one entry per currency; empty until read, after a failure,
	 * and when the api states none (its null is "balance unreadable", which the card shows no differently).
	 */
	credits: readonly ConvertedCredit[];
	/** Whether a listing has been read at all. False renders nothing, exactly as an empty list does. */
	loaded: boolean;
	/**
	 * Presses the holder's "turn it into credit toward a plan" for one purchase and answers what came of it, with the
	 * credit the api made on success. Never rejects: every refusal is an outcome the row states. It sends the Terms
	 * version this bundle shows and the credit the row names (`convertibleMinor`), so the api refuses the press when
	 * that figure moved, and answers `amountChanged` only once the listing is re-read: the row it is called with next
	 * must be the re-read one. A purchase that left the holder's hands leaves the shared list at once; every outcome
	 * re-reads the listing, because the credit moved.
	 */
	convert: (purchase: UnusedPurchase) => Promise<ConvertResult>;
}

/**
 * The account's one-off purchases, shared by every mounted surface through the resource cache
 * (`UNUSED_PURCHASES_CACHE_KEY`, see `./unusedPurchasesCache`).
 *
 * A thin React adapter by mandate (epic #26): which purchases are listed is the api's answer, what a row offers is
 * `unusedPurchases.rowActionOf`, how a declaration moves the list is `unusedPurchasesCache.noteWithdrawalSettled`, what
 * a refused conversion means is `convertOutcomeOf` and which of its outcomes leave the list is `conversionLeavesList`.
 * This hook only reads the listing and schedules the conversion.
 *
 * <b>Failures render nothing, and retry once per mount.</b> The cache stops revalidating after a failed
 * load — its policy, so an outage cannot become a request loop — which would leave a card that failed
 * once dark for the whole session. A MOUNT is the reader opening the sheet or starting a new transfer,
 * so one retry there is one request per reader action, never a loop.
 *
 * Mount it only for a signed-in reader: the endpoint is authenticated, and an anonymous read is a 401 the
 * cache would remember as a failure.
 */
export function useUnusedPurchases(): UnusedPurchasesVM {
	const {data, error, revalidate} = useCachedResource<PurchaseListing>(
		UNUSED_PURCHASES_CACHE_KEY, () => UnusedPurchasesApi.list());

	useEffect(() => {
		if (error !== null) {
			void revalidate();
		}
		// Mount-only on purpose: see the hook's comment. Re-running on `error` would retry on the failure
		// this very retry produced, which is the loop the cache's own policy exists to prevent.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const convert = useCallback(async (purchase: UnusedPurchase): Promise<ConvertResult> => {
		// A row that names no credit offers no press (`purchaseListingOf` makes both fields agree); nothing is sent
		// rather than a figure the buyer was never shown.
		if (purchase.convertibleMinor === null) {
			return {outcome: "unavailable", credited: null};
		}
		let result: ConvertResult;
		try {
			const answer = await UnusedPurchasesApi.convert(
				purchase.kind, purchase.purchaseId, PUBLISHED_TERMS_VERSION, purchase.convertibleMinor);
			result = {outcome: "converted", credited: {amountMinor: answer.creditedMinor, currency: answer.currency}};
		} catch (failure) {
			result = {outcome: convertOutcomeOf(failure), credited: null};
		}
		if (conversionLeavesList(result.outcome)) {
			// Out of the SHARED list now, not on the next background read: the note on the create panel must not keep
			// offering a purchase that is credit now or that the api says is spent or refunded.
			mutateCache<PurchaseListing>(UNUSED_PURCHASES_CACHE_KEY,
				previous => previous && listingWithout(previous, purchase.purchaseId));
		}
		if (result.outcome === "amountChanged") {
			// Waited for, unlike every other outcome: the confirm reopens on this answer and must already name the
			// credit the api would make now, or a second press could be sent with the figure that was just refused.
			await revalidate();
		} else {
			// The credit moved, or the api's word on this purchase did; the rows stay painted meanwhile.
			invalidateResource(UNUSED_PURCHASES_CACHE_KEY);
		}
		return result;
	}, [revalidate]);

	const listing = data ?? NO_PURCHASES;
	return {
		purchases: listing.purchases,
		withdrawableUsed: listing.withdrawableUsed,
		credits: listing.credits ?? [],
		loaded: data !== undefined,
		convert,
	};
}
